import { Spinner } from "@heroui/react";
import { Skeleton, useTheme } from "@mui/material";
import { useGetAuctionForms } from "apollo/hooks/queries";
import { isLoading } from "apollo/utils";
import clsx from "clsx";
import { useSenderAccountContext } from "context/SenderAccountContext";
import {
  isRehabFormType,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  RECARE_FILE_TYPE,
  REHAB_FORM_NAMES,
} from "core/consts";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { getIsDtaActive } from "core/model/productfeatures/utils";
import { openPdfInNewWindow } from "core/model/utils/urls";
import {
  Auction,
  AuctionForm,
  Patient,
  QueryProgress,
  QueryProgressActive,
  RehabFormName,
} from "core/types";
import { TrackingEventName } from "core/validationSchemas/tracking";
import ConfirmationDialog from "ds_legacy/components/ConfirmationDialog";
import LinkV2, { useExternalLinkLabel } from "ds_legacy/components/LinkV2";
import { LinkButton } from "ds_legacy/components/LinkV2/LinkButton";
import RSButton from "ds_legacy/components/RSButton";
import { CUSTOM_BLACK } from "ds_legacy/materials/colors";
import { HorizontalLayout } from "ds_legacy/materials/layouts";
import { margin, sizing } from "ds_legacy/materials/metrics";
import {
  Body,
  FONT_SIZE_14,
  FONT_SIZE_16,
  FONT_SIZE_20,
  FONT_SIZE_24,
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_REGULAR,
  LINE_HEIGHT_24,
  LINE_HEIGHT_26,
  Subheading,
  Title,
} from "ds_legacy/materials/typography";
import { Link } from "ds/ui";
import { useHL7ImportStatusContext } from "dsl/atoms/HL7ImportStatusContext";
import { useHandleFileDownload } from "dsl/ecosystems/PatientFiles/useDownloadFile";
import { useCreateRehaPdf } from "dsl/ecosystems/PatientForms/RehabForms/useCreateRehaPdf";
import { useRecareDocsContext } from "dsl/ecosystems/RecareDocs/context";
import { useFetchFiles } from "dsl/ecosystems/RecareDocs/Modal";
import { useGetFormData } from "dsl/ecosystems/RecareDocs/Modal/useDocsForm";
import { FORM_NAME_TO_FILE_TYPE } from "dsl/ecosystems/RecareDocs/Modal/utils";
import { useCareseekerPatientHomeUrls } from "dsl/hooks/useNavigationHandlers";
import {
  ArrowLeftIcon,
  ChevronRightIcon,
  ClipboardListIcon,
  LockIcon,
  PencilIcon,
  PrinterIcon,
  Trash2Icon,
} from "lucide-react";
import { useGetAHBMessageThreads } from "onprem/hooks";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";
import { AHBMessageSection } from "./AHBMessageSection";
import { getRehabFormConfig } from "./configs";
import { GkvInsuranceIkMismatchBanner } from "./GkvInsuranceIkMismatchBanner";
import { getRehabFormFillOutTrackEvent } from "./useAHBMessageSection";
import { getFormInputValue, useDeleteConfirmation } from "./utils";

type FormListItemProps = {
  auction: Auction;
  formName: RehabFormName;
  auctionForms: AuctionForm[];
  patient: Patient;
  queryProgress: QueryProgress;
  title: string;
};

type ActiveFormActionProps = {
  auction: Auction;
  formName: RehabFormName;
  auctionForms: AuctionForm[];
  patient: Patient;
};

type LinkToFormProps = {
  auction: Auction;
  formName: RehabFormName;
  label: string;
  patient: Patient;
  testId: string;
  trackEventProps?: Record<string, any> & { name: TrackingEventName };
};

function getFormStatus({
  auctionForms,
  formName,
}: {
  formName: RehabFormName;
  auctionForms?: AuctionForm[];
}) {
  if (!auctionForms?.length)
    return { isFormAvailable: false, isFormDisabled: false };

  const fileType = FORM_NAME_TO_FILE_TYPE[formName];
  const hasGKVForm = auctionForms.some(
    (form) =>
      form.form_type === RECARE_FILE_TYPE.GKV_GENERAL_FORM ||
      form.form_type === RECARE_FILE_TYPE.GKV_MEDICAL_FORM,
  );
  const hasDRVForm = auctionForms.some(
    (form) =>
      form.form_type === RECARE_FILE_TYPE.DRV_GENERAL_FORM ||
      form.form_type === RECARE_FILE_TYPE.DRV_MEDICAL_FORM,
  );

  const isGKVForm =
    fileType === RECARE_FILE_TYPE.GKV_GENERAL_FORM ||
    fileType === RECARE_FILE_TYPE.GKV_MEDICAL_FORM;
  const isDRVForm =
    fileType === RECARE_FILE_TYPE.DRV_GENERAL_FORM ||
    fileType === RECARE_FILE_TYPE.DRV_MEDICAL_FORM;

  return {
    isFormAvailable: auctionForms.some((form) => form.form_type === fileType),
    isFormDisabled: (isDRVForm && hasGKVForm) || (isGKVForm && hasDRVForm),
  };
}

export function StyledFormCard({
  children,
  className,
  isDisabled,
  testId,
}: {
  children?: ReactNode;
  className?: string;
  isDisabled?: boolean;
  testId?: string;
}) {
  return (
    <div
      className={clsx(
        "w-full rounded-[8px] border",
        {
          "border-gray-200 bg-gray-200 text-gray-600 shadow-none": isDisabled,
          "border-transparent bg-white shadow-[0px_2px_5px_0px_#00000026,0px_1px_3px_0px_#00000040]":
            !isDisabled,
        },
        className,
      )}
      data-disabled={isDisabled}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

function ActiveFormActions({
  auction,
  auctionForms,
  formName,
  patient,
}: ActiveFormActionProps) {
  const translations = useTranslations();
  const createRehaPdf = useCreateRehaPdf();

  const [progress, setProgress] = useState<QueryProgressActive>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const { goToRehabForms } = useCareseekerPatientHomeUrls();
  const { setFormModal } = useRecareDocsContext();
  const externalLinkLabel = useExternalLinkLabel();
  const { confirmProps, deleteFormProgress, onDelete } = useDeleteConfirmation({
    auction,
    formName,
  });
  const handleDownload = useHandleFileDownload({
    externalId: patient.external_id ?? "",
    target: "_blank",
  });
  const { isDocsAndExtractActive, isRedesignActive } =
    useIsDocsAndExtractActive();

  const { formData } = getFormInputValue({
    auctionForms,
    formName,
  });

  const { files } = useFetchFiles({
    auction,
    consultation: undefined,
    patient,
  });

  const { form } = useGetFormData({
    auctionForms: auctionForms ?? [],
    consultationForms: [],
    formName,
  });

  const matchedFile = useMemo(
    () =>
      files.find(
        ({ file }) =>
          "recare_file_type" in file &&
          file.recare_file_type === form?.form_type &&
          file.form_synced_with_file,
      ),
    [files, form?.form_type],
  );

  const handleOpenPdf = useCallback(async () => {
    if (isRedesignActive && isDocsAndExtractActive && matchedFile) {
      const result = await handleDownload(matchedFile.file);
      if (result) {
        window.open(result.url, "_blank");
        return;
      }
    }
    if (!patient.external_id) {
      throw new Error("Patient external ID is required");
    }
    if (!formData) {
      throw new Error("Form data is required");
    }
    const pdfBytes = await createRehaPdf({
      auctionForms,
      documentNumber: -1,
      externalId: patient.external_id,
      formData,
      formName,
    });
    openPdfInNewWindow(pdfBytes);
  }, [
    auctionForms,
    createRehaPdf,
    formData,
    formName,
    handleDownload,
    isDocsAndExtractActive,
    isRedesignActive,
    matchedFile,
    patient.external_id,
  ]);

  if (!formData) return null;

  return (
    <>
      <RSButton
        color="primary"
        id={`delete-${formName}`}
        LeftIcon={Trash2Icon}
        loading={deleteFormProgress}
        style={{ margin: margin(0) }}
        variant="text"
        onClick={() => onDelete()}
      >
        {translations.patientForms.menu.buttonDelete}
      </RSButton>
      <LinkButton
        aria-label={externalLinkLabel(
          translations.patientForms.menu.buttonPrint,
          "pdf",
        )}
        href=""
        id={`download-${formName}`}
        sx={{ margin: margin(0) }}
        type="text"
        onClick={async (e) => {
          e.preventDefault();
          try {
            setProgress(QUERY_PROGRESS_PENDING);
            await handleOpenPdf();
          } catch (err) {
            console.error("Error saving PDF", err);
          } finally {
            setProgress(QUERY_PROGRESS_NOT_STARTED);
          }
        }}
      >
        <span>
          {isLoading(progress) ? (
            <Spinner
              aria-valuemax={100}
              aria-valuemin={0}
              aria-valuetext={translations.general.loading}
              className="pr-2"
              role="progressbar"
              size="sm"
            />
          ) : (
            <PrinterIcon
              size={16}
              style={{ marginRight: sizing(1), opacity: 0.6 }}
            />
          )}
        </span>
        {translations.patientForms.menu.buttonPrint}
      </LinkButton>
      {isRedesignActive ? (
        <RSButton
          color="primary"
          id={`edit-form-${formName}`}
          LeftIcon={PencilIcon}
          loading="na"
          variant="text"
          onClick={() => {
            setFormModal(formName);
          }}
        >
          {translations.patientForms.menu.buttonEdit}
        </RSButton>
      ) : (
        <LinkButton
          id={`edit-form-${formName}`}
          sx={{ margin: margin(0) }}
          to={goToRehabForms({
            auctionId: auction.id,
            patientId: patient.id,
            type: formName,
          })}
          type="text"
        >
          <span>
            <PencilIcon
              size={16}
              style={{ marginRight: sizing(1), opacity: 0.6 }}
            />
          </span>
          {translations.patientForms.menu.buttonEdit}
        </LinkButton>
      )}
      <ConfirmationDialog {...confirmProps} />
    </>
  );
}

export const BackToFiles = ({
  auction,
  patient,
}: {
  auction: Auction;
  patient: Patient;
}) => {
  const translations = useTranslations();
  const { goToFiles } = useCareseekerPatientHomeUrls();
  const { isRedesignActive } = useIsDocsAndExtractActive();

  if (!isRedesignActive) return null;

  return (
    <Link
      className="mb-4"
      href={goToFiles({ auctionId: auction.id, patientId: patient.id })}
      id="back-to-files"
    >
      <span className="flex items-center gap-2">
        <ArrowLeftIcon className="h-4 w-4" />
        {translations.providersearch.providerDetailsPage.backToListing}
      </span>
    </Link>
  );
};

export function LinkToForm({
  auction,
  formName,
  label,
  patient,
  testId,
  trackEventProps,
}: LinkToFormProps) {
  const { trackEvent } = useTracking();
  const { goToRehabForms } = useCareseekerPatientHomeUrls();
  const { isRedesignActive } = useIsDocsAndExtractActive();
  const { setFormModal } = useRecareDocsContext();

  if (isRedesignActive) {
    return (
      <Link
        as="button"
        color="primary"
        endIcon={<ChevronRightIcon className="h-4 w-4" />}
        id={`open-form-${formName}`}
        onPress={() => {
          setFormModal(formName);
        }}
      >
        {label}
      </Link>
    );
  }

  return (
    <LinkV2
      data-testid={testId}
      to={goToRehabForms({
        auctionId: auction.id,
        patientId: patient.id,
        type: formName,
      })}
      onClick={() => {
        if (trackEventProps) trackEvent(trackEventProps);
      }}
    >
      <HorizontalLayout aligned as="span" overflow="visible">
        {label}
        <div>
          <ChevronRightIcon
            size={FONT_SIZE_16}
            style={{ fontSize: FONT_SIZE_16, margin: margin(0, 1, 0, 0) }}
          />
        </div>
      </HorizontalLayout>
    </LinkV2>
  );
}

function FormListItem({
  auction,
  auctionForms,
  formName,
  patient,
  queryProgress,
  title,
}: FormListItemProps) {
  const translations = useTranslations();
  const theme = useTheme();
  const { pdfConfig } = getRehabFormConfig(formName);
  const { isFormAvailable, isFormDisabled } = getFormStatus({
    auctionForms,
    formName,
  });

  if (isLoading(queryProgress)) {
    return (
      <Skeleton className="flex min-h-[78px] flex-col justify-center p-0" />
    );
  }

  return (
    <StyledFormCard
      className={clsx("flex flex-col justify-center p-4", {
        "px-4 py-3": isFormAvailable,
      })}
      isDisabled={isFormDisabled}
      testId={`form-card-${formName}`}
    >
      <div className="flex items-center justify-between">
        {isFormAvailable ? (
          <div className="flex flex-col gap-2">
            <Body as="p" fontSize={FONT_SIZE_14} margin={margin(0)}>
              {title}
            </Body>
            <div className="flex items-center gap-2">
              <ClipboardListIcon size={16} />
              <Body
                as="p"
                fontSize={FONT_SIZE_14}
                fontWeight={FONT_WEIGHT_BOLD}
                margin={margin(0)}
              >
                {pdfConfig.downloadPdfFileName}
              </Body>
              <LockIcon size={12} />
            </div>
          </div>
        ) : (
          <Body
            as="p"
            color={isFormDisabled ? theme.palette.grey[600] : undefined}
            fontSize={FONT_SIZE_14}
            margin={margin(0)}
          >
            {title}
          </Body>
        )}
        <div className="flex flex-col items-end justify-end">
          <div className="flex items-baseline space-x-2">
            {isFormAvailable ? (
              <ActiveFormActions
                auction={auction}
                auctionForms={auctionForms}
                formName={formName}
                patient={patient}
              />
            ) : !isFormDisabled ? (
              <LinkToForm
                auction={auction}
                formName={formName}
                label={translations.patientForms.menu.fillOutForm}
                patient={patient}
                testId={`go-to-form-${formName}`}
                trackEventProps={{
                  name: getRehabFormFillOutTrackEvent(formName),
                }}
              />
            ) : null}
          </div>
        </div>
      </div>
    </StyledFormCard>
  );
}

function AHBRequestSection({
  auction,
  auctionForms,
  patient,
  refetchAuctionForms,
}: {
  auction: Auction;
  auctionForms: AuctionForm[] | undefined;
  patient: Patient;
  refetchAuctionForms?: () => Promise<unknown>;
}) {
  const [queryProgress, ahbMessagesPayload, refetchAHBMessageThreads] =
    useGetAHBMessageThreads({
      dispatchOnMount: true,
      externalId: patient.external_id,
    });
  const { hasTwoWaySync } = useHL7ImportStatusContext();

  return (
    <AHBMessageSection
      ahbMessagesPayload={ahbMessagesPayload}
      auction={auction}
      auctionForms={auctionForms}
      hasTwoWaySync={hasTwoWaySync}
      patient={patient}
      queryProgress={queryProgress}
      refetchAHBMessageThreads={refetchAHBMessageThreads}
      refetchAuctionForms={refetchAuctionForms}
    />
  );
}

export function RehabFormOverviewList({
  auction,
  auctionForms,
  patient,
  queryProgress,
  refetchAuctionForms,
}: {
  auction: Auction;
  auctionForms: AuctionForm[];
  patient: Patient;
  queryProgress: QueryProgress;
  refetchAuctionForms?: () => Promise<unknown>;
}) {
  const translations = useTranslations();
  const { careseeker } = useSenderAccountContext();
  const isDtaActivated = getIsDtaActive({ careseeker });

  return (
    <div
      className={clsx("w-full max-w-5xl place-self-center p-8 pb-16", {
        "space-y-12": isDtaActivated,
      })}
    >
      <div className="flex flex-col space-y-4">
        <BackToFiles auction={auction} patient={patient} />
        {isDtaActivated ? (
          <div className="flex flex-col space-y-2">
            <Title
              as="h2"
              margin={margin(0)}
              style={{ fontSize: FONT_SIZE_24, fontWeight: FONT_WEIGHT_BOLD }}
            >
              {translations.insuranceApp.eligibilityTab.titleEligibility}
            </Title>
            <p className="text-[16px] leading-6 font-normal text-gray-600">
              {
                translations.insuranceApp.eligibilityTab
                  .sunsetEligibilityMessengerText
              }
            </p>
          </div>
        ) : (
          <div className="mt-4">
            <Title
              as="h2"
              margin={margin(0, 0, isDtaActivated ? 0 : 3)}
              style={{ fontSize: FONT_SIZE_24, fontWeight: FONT_WEIGHT_BOLD }}
            >
              {translations.insuranceApp.eligibilityTab.titleEligibility}
            </Title>
            <GkvInsuranceIkMismatchBanner
              auction={auction}
              auctionForms={auctionForms}
              className="mt-2"
              patient={patient}
              refetchAuctionForms={refetchAuctionForms}
            />
          </div>
        )}
        {isDtaActivated && (
          <AHBRequestSection
            auction={auction}
            auctionForms={auctionForms}
            patient={patient}
            refetchAuctionForms={refetchAuctionForms}
          />
        )}
      </div>
      <div className="flex w-full flex-col space-y-8">
        <div className="flex flex-col space-y-4">
          <div>
            <Title
              as="h2"
              margin={margin(0, 0, 0.5)}
              style={{
                fontSize: FONT_SIZE_20,
                fontWeight: FONT_WEIGHT_BOLD,
                lineHeight: LINE_HEIGHT_26,
              }}
            >
              {translations.patientForms.menu.titleFormSection}
            </Title>
            <p
              style={{
                color: CUSTOM_BLACK,
                fontSize: FONT_SIZE_14,
                fontWeight: FONT_WEIGHT_REGULAR,
                lineHeight: LINE_HEIGHT_24,
              }}
            >
              {translations.patientForms.menu.subtitleFormSection}
              <br />
              <span className="inline-flex items-center">
                <LockIcon aria-hidden="true" className="mr-1" size={12} />
                {translations.patientForms.menu.encryptionInformation}
              </span>
            </p>
          </div>
          <div>
            <Subheading
              as="h3"
              bold
              margin={margin(0, 0, 0.5, 0)}
              style={{ fontSize: FONT_SIZE_16 }}
            >
              {translations.patientForms.menu.titleGkvForms}
            </Subheading>
            <div className="flex flex-col space-y-3">
              <FormListItem
                auction={auction}
                auctionForms={auctionForms}
                formName={REHAB_FORM_NAMES.GKV_GENERAL}
                patient={patient}
                queryProgress={queryProgress}
                title={translations.patientForms.menu.rehabGeneralForm}
              />
              <FormListItem
                auction={auction}
                auctionForms={auctionForms}
                formName={REHAB_FORM_NAMES.GKV_MEDICAL}
                patient={patient}
                queryProgress={queryProgress}
                title={translations.patientForms.menu.rehabMedicalForm}
              />
            </div>
          </div>
        </div>
        <div>
          <Subheading
            as="h3"
            bold
            margin={margin(0, 0, 0.5, 0)}
            style={{ fontSize: FONT_SIZE_16 }}
          >
            {translations.patientForms.menu.titleDrvForms}
          </Subheading>
          <div className="flex flex-col space-y-3">
            <FormListItem
              auction={auction}
              auctionForms={auctionForms}
              formName={REHAB_FORM_NAMES.DRV_GENERAL}
              patient={patient}
              queryProgress={queryProgress}
              title={translations.patientForms.menu.drvGeneralForm}
            />
            <FormListItem
              auction={auction}
              auctionForms={auctionForms}
              formName={REHAB_FORM_NAMES.DRV_MEDICAL}
              patient={patient}
              queryProgress={queryProgress}
              title={translations.patientForms.menu.drvMedicalForm}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PatientRehabFormsHome({
  auction,
  patient,
}: {
  auction: Auction;
  patient: Patient;
}) {
  const [queryProgress, auctionForms, { refetch: refetchAuctionForms }] =
    useGetAuctionForms({
      auctionId: auction.id,
      fetchPolicy: "network-only",
    });

  const forms = auctionForms?.filter((form) => isRehabFormType(form.form_type));

  return (
    <RehabFormOverviewList
      auction={auction}
      auctionForms={forms ?? []}
      patient={patient}
      queryProgress={queryProgress}
      refetchAuctionForms={refetchAuctionForms}
    />
  );
}
